/**
 * Referrals Relationship API Extension
 *
 * Custom API client for relationship-based referral queries.
 * This extension uses the custom backend route that properly handles
 * snake_case query parameters for sender_id and receiver_id filtering.
 *
 * The backend endpoint adds computed fields to the response:
 * - status (from referral_statuses table join)
 * - senderName, senderTrade, senderProfileImage (from user_profiles)
 * - receiverName, receiverTrade, receiverProfileImage (from user_profiles)
 *
 * ISSUE FIX: The generated API client was sending snake_case params but the
 * generated backend route expected camelCase. This extension bypasses the
 * generated route and uses our custom /by-relationship endpoint.
 */

import { httpClient } from '../http-client.js';
import type { ReferralsRow } from '../../types/generated_schemas/generated-referrals-schema.js';

/**
 * Extended referral type that includes computed fields added by the backend
 */
export interface ReferralWithPartnerData extends ReferralsRow {
  status?: string;
  senderName?: string | null;
  senderTrade?: string | null;
  senderProfileImage?: string | null;
  receiverName?: string | null;
  receiverTrade?: string | null;
  receiverProfileImage?: string | null;
}

export interface ReferralRelationshipOptions {
  senderId: string;
  receiverId: string;
  statusId?: number;
  outcome?: string;
  limit?: number;
  offset?: number;
}

export interface ReferralsResponse {
  data: ReferralWithPartnerData[];
  success: boolean;
}

export interface ReferralsCountResponse {
  data: number;
  success: boolean;
}

/**
 * Get referrals between two specific users (sender → receiver)
 *
 * @param options - Relationship query options
 * @returns Promise resolving to array of referrals records with partner profile data
 */
export async function getReferralsByRelationship(
  options: ReferralRelationshipOptions
): Promise<ReferralWithPartnerData[]> {
  const params = new URLSearchParams();

  // Use snake_case for API parameters
  params.set('sender_id', options.senderId);
  params.set('receiver_id', options.receiverId);

  if (options.statusId) params.set('status_id', options.statusId.toString());
  if (options.outcome) params.set('outcome', options.outcome);
  if (options.limit) params.set('limit', options.limit.toString());
  if (options.offset) params.set('offset', options.offset.toString());

  const queryString = params.toString();
  const url = `/referrals-relationship-queries/custom/by-relationship?${queryString}`;

  const response = await httpClient.get<ReferralsResponse>(url);

  // Return the response data - includes computed 'status' field and partner profile data from backend
  return response.data;
}

/**
 * Get count of referrals between two specific users
 *
 * @param options - Relationship query options
 * @returns Promise resolving to count of referrals
 */
export async function getReferralsByRelationshipCount(
  options: Pick<ReferralRelationshipOptions, 'senderId' | 'receiverId' | 'statusId' | 'outcome'>
): Promise<number> {
  const params = new URLSearchParams();

  params.set('sender_id', options.senderId);
  params.set('receiver_id', options.receiverId);

  if (options.statusId) params.set('status_id', options.statusId.toString());
  if (options.outcome) params.set('outcome', options.outcome);

  const queryString = params.toString();
  const url = `/referrals-relationship-queries/custom/by-relationship/count?${queryString}`;

  const response = await httpClient.get<ReferralsCountResponse>(url);
  return response.data;
}
